import clsx from "clsx";
import { type ReactNode, useId } from "react";
import Tooltip from "../../common/Tooltip/Tooltip";
import styles from "../RadioButtons/RadioButtons.module.scss";
import Radio, { type RadioProps } from "./Radio";
import { useRadioGroup } from "./RadioGroup";

export interface RadioButtonProps<TValue extends string = string>
  extends Omit<RadioProps<TValue>, "className" | "size"> {
  className?: string;
  children: ReactNode;
  /** Shown on hover. Pair an icon-only button with this and an `aria-label` for its accessible name. */
  tooltip?: ReactNode;
  /** Square padding for a button whose content is a single icon. */
  iconOnly?: boolean;
}

const RadioButton = <TValue extends string = string>({
  children,
  className,
  checked,
  disabled,
  value,
  tooltip,
  iconOnly = false,
  ...props
}: RadioButtonProps<TValue>) => {
  const group = useRadioGroup();
  const inputId = useId();
  const selected = checked ?? (group ? group.value === value : false);
  const optionDisabled = disabled ?? group?.disabled;

  const label = (
    <label
      className={clsx(styles.button, className)}
      htmlFor={inputId}
      data-icon-only={iconOnly ? "true" : undefined}
    >
      {children}
    </label>
  );

  return (
    <div
      className={styles.choice}
      data-selected={selected ? "true" : "false"}
      data-disabled={optionDisabled ? "true" : undefined}
    >
      <Radio
        {...props}
        id={inputId}
        className={styles.input}
        value={value}
        checked={checked}
        disabled={disabled}
      />
      {/* The tooltip hangs off the visible label: the radio input itself is visually hidden. */}
      {tooltip ? (
        <Tooltip
          title={tooltip}
          asChild
        >
          {label}
        </Tooltip>
      ) : (
        label
      )}
    </div>
  );
};

export default RadioButton;
